import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";

import { SCAN_RUNS_LIMIT, SCAN_RUNS_LIMIT_STORAGE_KEY } from "./components/Table/constants";
import { SEARCH_SCAN_RUNS, SEARCH_SCAN_RUNS_COUNT } from "./gql";

const useScanRuns = () => {
  const { onError } = useTypedContext(FlashContext);
  const { scanId = "" } = useParams<{ scanId: string }>();
  const [storedRowsPerPage] = useLocalStorage<number>(SCAN_RUNS_LIMIT_STORAGE_KEY);

  const initialVariables = useMemo(
    () => ({
      id: scanId,
      input: {
        first: storedRowsPerPage || SCAN_RUNS_LIMIT,
        after: null,
        fullTextSearch: null,
        predicates: [],
        orderBy: null,
        requestedPage: null,
      },
    }),
    [scanId, storedRowsPerPage]
  );

  const { error, loading, data, previousData, networkStatus, refetch } = usePolledQuery(
    SEARCH_SCAN_RUNS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
      skip: !scanId,
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    SEARCH_SCAN_RUNS_COUNT,
    {
      variables: { id: scanId, input: { fields: [], fullTextSearch: null, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
      skip: !scanId,
    }
  );

  const loadScanRuns = useCallback(
    async ({
      cursor,
      rowsPerPage,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      if (!scanId) {
        return;
      }

      try {
        await refetch({
          id: scanId,
          input: {
            ...initialVariables.input,
            after: cursor,
            first: rowsPerPage,
            requestedPage,
          },
        });
      } catch (refetchError) {
        onError(refetchError);
      }
    },
    [onError, initialVariables, refetch, scanId]
  );

  const searchRuns = data?.scan.searchRuns || previousData?.scan.searchRuns;

  const runs = useMemo(() => searchRuns?.edges.map((edge) => edge.node) || [], [searchRuns?.edges]);

  const totalCount =
    countData?.scan.searchRunsSuggestions.filteredCount ??
    previousCountData?.scan.searchRunsSuggestions.filteredCount ??
    0;

  const hasRows = runs.length > 0;

  const loadingContent =
    !!scanId &&
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  return {
    runs,
    error,
    loadingContent,
    hasRows,
    loadScanRuns,
    endCursor: searchRuns?.pageInfo.endCursor,
    startCursor: searchRuns?.pageInfo.startCursor,
    totalCount,
  };
};

export default useScanRuns;
